<script>
import MessageMixin from '@mixins/messageMixin';

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif'];
const VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
const MAX_IMAGE_SIZE = 5 * 1000 * 1000;
const MAX_VIDEO_SIZE = 100 * 1000 * 1000;
const MAX_VIDEO_DURATION = 60;

export default {
  name: 'MessageMediaMenu',
  mixins: [MessageMixin],
  props: {
    media: {
      type: Array,
      required: true,
    },
  },
  emits: ['update:media'],
  data() {
    return {
      showMediaMenu: false,
      menuStyle: {},
    };
  },
  mounted() {
    this._mediaMenuClose = (e) => {
      if (this.showMediaMenu && !this.$refs.mediaMenu?.contains(e.target) && !this.$refs.menu?.contains(e.target)) {
        this.showMediaMenu = false;
      }
    };
    document.addEventListener('click', this._mediaMenuClose, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this._mediaMenuClose, true);
  },
  methods: {
    updateMenuPosition() {
      const rect = this.$refs.trigger.getBoundingClientRect();
      this.menuStyle = {
        left: `${rect.left}px`,
        bottom: `${window.innerHeight - rect.top + 8}px`,
      };
    },
    toggleMediaMenu() {
      if (!this.showMediaMenu) this.updateMenuPosition();
      this.showMediaMenu = !this.showMediaMenu;
    },
    openFilePicker(type) {
      this.showMediaMenu = false;
      this.$refs[type === 'image' ? 'imageInput' : 'videoInput'].click();
    },
    async onFilesSelected(event) {
      const files = Array.from(event.target.files);
      event.target.value = '';
      const media = [...this.media];
      for (const file of files) {
        const isVideo = file.type.startsWith('video/');
        const error = isVideo ? await this.getVideoError(file) : this.getImageError(file);
        if (error) {
          this.addError(error);
          continue;
        }
        const url = URL.createObjectURL(file);
        media.push({file, url, type: isVideo ? 'video' : 'image'});
      }
      this.$emit('update:media', media);
    },
    getImageError(file) {
      if (!IMAGE_TYPES.includes(file.type)) return 'Format d’image non supporté (PNG, JPEG ou GIF uniquement).';
      if (file.size > MAX_IMAGE_SIZE) return 'L’image dépasse la taille maximale de 5 Mo.';
      return null;
    },
    getVideoError(file) {
      if (!VIDEO_TYPES.includes(file.type)) {
        return Promise.resolve('Format de vidéo non supporté (MP4 ou MOV uniquement).');
      }
      if (file.size > MAX_VIDEO_SIZE) return Promise.resolve('La vidéo dépasse la taille maximale de 100 Mo.');
      return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(url);
          resolve(video.duration > MAX_VIDEO_DURATION ? 'La vidéo dépasse la durée maximale de 60 secondes.' : null);
        };
        video.onerror = () => {
          URL.revokeObjectURL(url);
          resolve('Impossible de lire cette vidéo.');
        };
        video.src = url;
      });
    },
  },
};
</script>

<template>
  <div class="relative" ref="mediaMenu">
    <button
      ref="trigger"
      class="h-12 w-12 flex justify-center items-center bg-grey-50 text-grey-900 rounded-full hover:bg-grey-100 focus-visible:bg-grey-100"
      @click="toggleMediaMenu">
      <span class="ga-icon ga-plus text-xl font-bold" />
    </button>
    <Teleport to="body">
      <div
        v-if="showMediaMenu"
        ref="menu"
        class="fixed bg-white rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] border border-grey-100 min-w-[160px] z-50 p-2"
        :style="menuStyle">
        <button
          class="w-full text-left px-2.5 py-2 font-medium text-grey-900 hover:bg-grey-50 flex items-center gap-2 rounded-md leading-[18px]"
          @click="openFilePicker('image')">
          <span class="ga-icon ga-image text-grey-900 text-lg" />
          Image
        </button>
        <button
          class="w-full text-left px-2.5 py-2 font-medium text-grey-900 hover:bg-grey-50 flex items-center gap-2 rounded-md leading-[18px]"
          @click="openFilePicker('video')">
          <span class="ga-icon ga-video text-grey-900 text-lg" />
          Vidéo
        </button>
      </div>
    </Teleport>

    <input
      ref="imageInput"
      type="file"
      accept="image/png,image/jpeg,image/gif"
      multiple
      class="hidden"
      @change="onFilesSelected" />
    <input
      ref="videoInput"
      type="file"
      accept="video/mp4,video/quicktime"
      multiple
      class="hidden"
      @change="onFilesSelected" />
  </div>
</template>
